
import RevisitDialog from '~/components/lazyloads/dialogs/RevisitDialog.vue'

const RECAPTCHA_SCRIPT_ID =
  'myuse-google-recaptcha'

const THIRTY_DAYS_MS =
  1000 * 60 * 60 * 24 * 30

export default {
  name: 'Welcome',

  layout: 'layout-landing',

  transition: 'myuse-fade',

  components: {
    RevisitDialog,
  },

  async asyncData({ $content }) {
    const page = await $content(
      'general/index'
    ).fetch()

    return {
      page,
    }
  },

  data() {
    return {
      page: null,

      isMounted: false,
      show_confirm_age: true,
      show_captcha: true,
      allow_begin: false,

      bot: '',
      batch_id: 0,
      age_confirmed: false,
      isTesting: false,
      remember_me: 0,

      // Legacy spelling retained for compatibility.
      has_completed_capthca: false,

      last_login: null,
      show_revisit: false,
      user: null,

      captchaType: 'text',
      captcha_content: null,
      captcha_key: null,
      captcha_code: null,

      recaptchaWidgetId: null,
      recaptchaLoading: false,
      recaptchaError: null,

      registrationLoading: false,
    }
  },

  computed: {
    isPWA() {
      if (
        process.server ||
        typeof window === 'undefined'
      ) {
        return false
      }

      const standaloneDisplay =
        window.matchMedia &&
        window.matchMedia(
          '(display-mode: standalone)'
        ).matches

      const iosStandalone =
        window.navigator &&
        window.navigator.standalone === true

      return Boolean(
        standaloneDisplay ||
        iosStandalone
      )
    },

    captchaRules() {
      return [
        (value) =>
          Boolean(value) ||
          'You must complete the captcha.',

        (value) =>
          !value ||
          value.length === 5 ||
          'The CAPTCHA code must contain 5 characters.',
      ]
    },
  },

  watch: {
    captcha_code(value) {
      if (
        this.captchaType === 'text' &&
        value &&
        value.length === 5 &&
        !this.registrationLoading
      ) {
        window.setTimeout(() => {
          this.submitTextCaptcha()
        }, 100)
      }
    },
  },

  created() {
    this.$nuxt.$on(
      'reloadCookieDisplay',
      this.syncRememberMe
    )
  },

  mounted() {
    this.initialisePage()
  },

  beforeDestroy() {
    this.$nuxt.$off(
      'reloadCookieDisplay',
      this.syncRememberMe
    )

    this.resetRecaptchaWidget()
  },

  methods: {
    async initialisePage() {
      this.syncRememberMe()

      if (
        this.$config.APP_ENV ===
        'development'
      ) {
        this.isTesting = true
        this.batch_id = 99
      }

      this.restoreStoredUser()

      if (
        !this.user &&
        this.captchaType === 'text'
      ) {
        await this.generateCaptcha()
      }

      this.isMounted = true
    },

    syncRememberMe() {
      const value =
        this.$store.getters[
          'auth/getRememberMeCookieStatus'
        ]

      this.remember_me =
        Number(value) === 1
          ? 1
          : 0
    },

    restoreStoredUser() {
      const storedUser =
        this.$store.getters[
          'auth/getUser'
        ]

      const storedLastLogin =
        this.$store.getters[
          'auth/getLastLogin'
        ]

      if (!storedUser) {
        this.user = null
        this.last_login = null
        this.show_revisit = false
        return
      }

      this.user = storedUser
      this.last_login =
        storedLastLogin || null

      this.has_completed_capthca = true
      this.age_confirmed = true
      this.allow_begin = true

      this.logoutIfExpired()
    },

    logoutIfExpired() {
      const rememberEnabled =
        Number(
          this.$store.getters[
            'auth/getRememberMeCookieStatus'
          ]
        ) === 1

      if (!rememberEnabled) {
        this.show_revisit = false
        return
      }

      const lastLogin =
        this.$store.getters[
          'auth/getLastLogin'
        ] ||
        this.last_login

      const timestamp =
        lastLogin
          ? new Date(lastLogin).getTime()
          : NaN

      if (!Number.isFinite(timestamp)) {
        this.show_revisit = false
        return
      }

      const elapsed =
        Date.now() - timestamp

      if (
        elapsed < 0 ||
        elapsed > THIRTY_DAYS_MS
      ) {
        this.$store.dispatch(
          'auth/logoutUser',
          {
            invisible: true,
          }
        )

        this.user = null
        this.last_login = null
        this.has_completed_capthca = false
        this.age_confirmed = false
        this.allow_begin = false
        this.show_revisit = false

        return
      }

      this.show_revisit = true

      this.$nextTick(() => {
        const dialog =
          this.$refs.dialog

        if (
          dialog &&
          typeof dialog.openDialog ===
            'function'
        ) {
          dialog.openDialog()
        }
      })
    },

    onRememberToggle(value) {
      const enabled =
        Number(value) === 1
          ? 1
          : 0

      this.remember_me = enabled

      this.$store.dispatch(
        'auth/setRememberMePreference',
        enabled
      )

      if (!enabled) {
        this.show_revisit = false
      }
    },

    async onBegin() {
      if (!this.allow_begin) {
        return
      }

      try {
        await this.$store.dispatch(
          'navigation/goToMenu'
        )
      } catch (_) {
        if (
          this.$route.path !==
          '/dashboard'
        ) {
          await this.$router.push(
            '/dashboard'
          )
        }
      }
    },

    async generateCaptcha() {
      if (
        this.captchaType !== 'text' ||
        this.user ||
        this.has_completed_capthca
      ) {
        return
      }

      this.captcha_content = null
      this.captcha_key = null
      this.captcha_code = null

      try {
        const response =
          await this.$axios.$get(
            `${this.$config.BASE_API}/auth/get_captcha`
          )

        this.captcha_content =
          response.image

        this.captcha_key =
          response.key
      } catch (_) {
        this.$dialog.message.error(
          'The CAPTCHA could not be loaded. Please try again.',
          {
            position: 'top',
            color: 'red',
            timeout: 4000,
          }
        )
      }
    },

    async submitTextCaptcha() {
      if (
        this.captchaType !== 'text' ||
        this.registrationLoading ||
        this.user ||
        this.bot
      ) {
        return
      }

      if (
        !this.captcha_code ||
        this.captcha_code.length !== 5 ||
        !this.captcha_key
      ) {
        return
      }

      await this.registerUser({
        key: this.captcha_key,
        captcha: this.captcha_code,
      })
    },

    async onReCaptchaSuccess(token) {
      if (
        this.registrationLoading ||
        this.user ||
        this.bot
      ) {
        return
      }

      if (!token) {
        this.onReCaptchaError()
        return
      }

      await this.registerUser({
        recaptcha_token: token,
      })
    },

    async registerUser(captchaPayload) {
      if (
        this.registrationLoading ||
        this.bot
      ) {
        return
      }

      if (this.user) {
        this.completeRegistration()
        return
      }

      this.registrationLoading = true
      this.recaptchaError = null

      try {
        const response =
          await this.$store.dispatch(
            'auth/register_user',
            {
              batch_id: this.batch_id,
              domain:
                window.location.hostname,
              ...captchaPayload,
            }
          )

        this.user =
          response &&
          response.user
            ? response.user
            : response

        this.completeRegistration()

        if (!this.age_confirmed) {
          this.$dialog.message.success(
            'Correct! Please confirm you are over 18.',
            {
              position: 'top',
              color: 'green',
              timeout: 3000,
            }
          )
        }
      } catch (error) {
        this.handleRegistrationError(
          error,
          captchaPayload
        )
      } finally {
        this.registrationLoading = false
      }
    },

    completeRegistration() {
      this.has_completed_capthca = true
      this.allow_begin =
        this.age_confirmed === true
    },

    handleRegistrationError(
      error,
      captchaPayload
    ) {
      const responseData =
        error &&
        error.response &&
        error.response.data

      const apiMessage =
        responseData &&
        typeof responseData === 'object'
          ? responseData.message
          : responseData

      const usingRecaptcha =
        Boolean(
          captchaPayload &&
          captchaPayload.recaptcha_token
        )

      const fallbackMessage =
        usingRecaptcha
          ? 'Google reCAPTCHA verification failed.'
          : 'Incorrect CAPTCHA. Please try again.'

      const message =
        typeof apiMessage === 'string'
          ? apiMessage
          : fallbackMessage

      this.$dialog.message.error(
        message,
        {
          position: 'top',
          color: 'red',
          timeout: 4000,
        }
      )

      this.has_completed_capthca = false
      this.allow_begin = false

      if (usingRecaptcha) {
        this.recaptchaError = message
        this.resetRecaptchaWidget()
      } else {
        this.generateCaptcha()
      }
    },

    confirmAge(value) {
      this.age_confirmed =
        value === true

      if (!this.age_confirmed) {
        this.allow_begin = false
        return
      }

      if (this.user) {
        this.allow_begin = true
        return
      }

      if (
        this.$config.APP_ENV ===
        'development'
      ) {
        this.allow_begin = true
      }
    },

    async toggleCaptcha() {
      if (
        this.user ||
        this.has_completed_capthca ||
        this.registrationLoading ||
        this.recaptchaLoading
      ) {
        return
      }

      if (this.captchaType === 'text') {
        this.captchaType = 'google'

        this.captcha_code = null
        this.captcha_key = null
        this.captcha_content = null
        this.recaptchaError = null

        await this.$nextTick()
        await this.waitForBrowserPaint()
        await this.renderRecaptcha()

        return
      }

      this.captchaType = 'text'
      this.recaptchaError = null
      this.resetRecaptchaWidget()

      await this.$nextTick()
      await this.generateCaptcha()
    },

    waitForBrowserPaint() {
      if (
        typeof window === 'undefined' ||
        typeof window.requestAnimationFrame !==
          'function'
      ) {
        return Promise.resolve()
      }

      return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(
            resolve
          )
        })
      })
    },

    loadRecaptchaScript() {
      if (
        process.server ||
        typeof window === 'undefined'
      ) {
        return Promise.reject(
          new Error(
            'Google reCAPTCHA can only load in the browser.'
          )
        )
      }

      if (
        window.grecaptcha &&
        typeof window.grecaptcha.render ===
          'function'
      ) {
        return Promise.resolve(
          window.grecaptcha
        )
      }

      if (
        window.__myuseRecaptchaPromise
      ) {
        return window.__myuseRecaptchaPromise
      }

      window.__myuseRecaptchaPromise =
        new Promise(
          (resolve, reject) => {
            let attempts = 0

            const checkApi = () => {
              if (
                window.grecaptcha &&
                typeof window.grecaptcha
                  .render === 'function'
              ) {
                resolve(
                  window.grecaptcha
                )
                return
              }

              attempts += 1

              if (attempts >= 100) {
                window.__myuseRecaptchaPromise =
                  null

                reject(
                  new Error(
                    'Google reCAPTCHA did not initialise.'
                  )
                )

                return
              }

              window.setTimeout(
                checkApi,
                100
              )
            }

            const existingScript =
              document.getElementById(
                RECAPTCHA_SCRIPT_ID
              )

            if (existingScript) {
              checkApi()
              return
            }

            const script =
              document.createElement(
                'script'
              )

            script.id =
              RECAPTCHA_SCRIPT_ID

            script.src =
              'https://www.google.com/recaptcha/api.js?render=explicit'

            script.async = true
            script.defer = true

            script.onload =
              checkApi

            script.onerror = () => {
              window.__myuseRecaptchaPromise =
                null

              reject(
                new Error(
                  'Google reCAPTCHA could not be loaded.'
                )
              )
            }

            document.head.appendChild(
              script
            )
          }
        )

      return window.__myuseRecaptchaPromise
    },

    async renderRecaptcha() {
      this.recaptchaLoading = true
      this.recaptchaError = null

      try {
        const siteKey =
          this.$config
            .RECAPTCHA_SITE_KEY

        if (!siteKey) {
          throw new Error(
            'The Google reCAPTCHA site key is not configured.'
          )
        }

        const grecaptcha =
          await this.loadRecaptchaScript()

        await this.$nextTick()
        await this.waitForBrowserPaint()

        const container =
          this.$refs.recaptcha

        if (!container) {
          throw new Error(
            'The Google reCAPTCHA container could not be initialised.'
          )
        }

        if (
          this.recaptchaWidgetId !== null
        ) {
          grecaptcha.reset(
            this.recaptchaWidgetId
          )

          return
        }

        this.recaptchaWidgetId =
          grecaptcha.render(
            container,
            {
              sitekey: siteKey,

              callback:
                this.onReCaptchaSuccess,

              'expired-callback':
                this.onReCaptchaExpired,

              'error-callback':
                this.onReCaptchaError,
            }
          )
      } catch (error) {
        const message =
          error &&
          error.message
            ? error.message
            : 'Google reCAPTCHA is currently unavailable.'

        this.recaptchaError = message

        this.$dialog.message.error(
          message,
          {
            position: 'top',
            color: 'red',
            timeout: 5000,
          }
        )
      } finally {
        this.recaptchaLoading = false
      }
    },

    onReCaptchaExpired() {
      this.has_completed_capthca = false
      this.allow_begin = false

      this.recaptchaError =
        'The reCAPTCHA expired. Please complete it again.'

      this.resetRecaptchaWidget()
    },

    onReCaptchaError() {
      this.has_completed_capthca = false
      this.allow_begin = false

      this.recaptchaError =
        'Google reCAPTCHA encountered an error. Please retry or switch to the text CAPTCHA.'
    },

    resetRecaptchaWidget() {
      if (
        typeof window === 'undefined' ||
        !window.grecaptcha ||
        this.recaptchaWidgetId === null
      ) {
        return
      }

      try {
        window.grecaptcha.reset(
          this.recaptchaWidgetId
        )
      } catch (_) {
        // The widget may already have been
        // removed during route navigation.
      }
    },
  },

  head() {
    return {
      title: 'Welcome!',

      htmlAttrs: {
        class:
          this.$vuetify.breakpoint.mobile
            ? 'mobile-font'
            : 'normal-font',
      },

      meta: [
        {
          hid: 'description',
          name: 'description',
          content:
            'MyUSE (My Understanding Substance Use Experiences) is a support and harm-reduction web application, providing support to higher-education students who want to understand their illicit substance use experiences and access relevant resources.',
        },
      ],

      appleStatusBarStyle: 'primary',
    }
  },
}
